import React, { useState, useEffect } from 'react';
import './styles/Contact.css';

const FORMSUBMIT_EMAIL = 'kalavaanya.studio@gmail.com';
const FORMSUBMIT_ACTION = `https://formsubmit.co/${FORMSUBMIT_EMAIL}`;
const FORMSUBMIT_AJAX = `https://formsubmit.co/ajax/${FORMSUBMIT_EMAIL}`;
const TOAST_DURATION_MS = 4000;
const SUCCESS_MESSAGE = 'Form submitted successfully';

const emptyForm = {
  name: '',
  phone: '',
  email: '',
  inquiryType: '',
  message: ''
};

const Contact = () => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // _next will redirect FormSubmit back here; the marker triggers the toast on reload (Case A).
  const nextUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${window.location.pathname}?submitted=1#contact`
      : '';

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(null), TOAST_DURATION_MS);
  };

  // Case A: detect ?submitted=1 after a no-JS / reload-style submit and clean it from the URL.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('submitted') !== '1') return;

    showToast(SUCCESS_MESSAGE);
    params.delete('submitted');
    const search = params.toString();
    const cleanUrl =
      window.location.pathname +
      (search ? `?${search}` : '') +
      window.location.hash;
    window.history.replaceState({}, '', cleanUrl);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = (data) => {
    const errs = [];
    if (!data.name || data.name.trim().length < 2) {
      errs.push('Name must be at least 2 characters long');
    }
    if (!data.phone || !/^[+]?[0-9\s\-()]{10,}$/.test(data.phone)) {
      errs.push('Please enter a valid phone number');
    }
    if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      errs.push('Please enter a valid email address');
    }
    if (!data.inquiryType) {
      errs.push('Please select an inquiry type');
    }
    if (!data.message || data.message.trim().length < 10) {
      errs.push('Message must be at least 10 characters long');
    }
    return errs;
  };

  // Case B: intercept, validate, POST via fetch, show toast — no reload.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const honeypot = e.target.elements._honey?.value;
    if (honeypot) return; // bot-filled honeypot — silently drop

    const validationErrors = validateForm(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setTimeout(() => setErrors([]), 7000);
      return;
    }
    setErrors([]);

    setSubmitting(true);
    try {
      const res = await fetch(FORMSUBMIT_AJAX, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: 'New Kalavaanya Lead!',
          _captcha: 'false'
        })
      });
      if (!res.ok) throw new Error(`FormSubmit responded ${res.status}`);

      setFormData(emptyForm);
      showToast(SUCCESS_MESSAGE);
    } catch (err) {
      setErrors(['Something went wrong sending your message. Please try again or email us directly.']);
      setTimeout(() => setErrors([]), 7000);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="container">
        <h2 className="section-title">Connect With Us</h2>
        <p className="section-subtitle">Begin your journey in the sacred art of Bharatanatyam</p>

        <div className="contact-content">
          <div className="contact-info">
            <h3>Get in Touch</h3>
            <div className="contact-item">
              <span className="contact-label">WhatsApp:</span>
              <a href="https://wa.me/918332934280" target="_blank" rel="noopener noreferrer" className="contact-link">+91 8977 93280</a>
            </div>
            <div className="contact-item">
              <span className="contact-label">Email:</span>
              <a href="mailto:kalavaanya.studio@gmail.com" target="_blank" rel="noopener noreferrer" className="contact-link">kalavaanya.studio@gmail.com</a>
            </div>
            <div className="contact-item">
              <span className="contact-label">Instagram:</span>
              <a href="https://www.instagram.com/kalavaanya/" target="_blank" rel="noopener noreferrer" className="contact-link">@kalavaanya</a>
            </div>

            <blockquote className="contact-quote">
              "Every student is a universe of potential. At Kalavaanya, we don't just teach dance—we help souls discover their divine rhythm."
            </blockquote>
          </div>

          <div className="contact-form-container">
            {errors.length > 0 && (
              <div className="form-message error">
                <div>Please fix the following errors:</div>
                {errors.map((line, i) => (
                  <div key={i}>• {line}</div>
                ))}
              </div>
            )}

            <form
              className="contact-form"
              action={FORMSUBMIT_ACTION}
              method="POST"
              onSubmit={handleSubmit}
            >
              <input type="hidden" name="_subject" value="New Kalavaanya Lead!" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_next" value={nextUrl} />
              <input
                type="text"
                name="_honey"
                tabIndex="-1"
                autoComplete="off"
                className="hp-field"
                aria-hidden="true"
              />

              <div className="form-group">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="inquiryType">Inquiry Type</label>
                <select
                  id="inquiryType"
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select an option</option>
                  <option value="trial-class">Trial Class</option>
                  <option value="regular-classes">Regular Classes</option>
                  <option value="online-classes">Online Classes</option>
                  <option value="full-term">Full-term Training</option>
                  <option value="performance">Performance Opportunities</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                ></textarea>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? 'Sending…' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {toast && (
        <div className="toast" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </section>
  );
};

export default Contact;
